import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DialogConfirm, DialogConfirmData } from 'app/dialogs/confirm/dialog-confirm';
import { DialogPensionIncreaseClaimsCreateResults } from 'app/dialogs/pension/pension-increase-claims-create-results/dialog-pension-increase-claims-create-results.component';
import { base64ToArrayBuffer, saveByteArray } from 'app/helpers/blobDownload';
import { formatSsn } from 'app/helpers/formatSsn';
import { ExposedCreateInvoicePensionIncreaseReq } from 'app/interfaces/requests/Exposed/exposedCreateInvoicePensionIncreaseReq';
import { ExposedListKriaPensionIncreaseClaimsRes } from 'app/interfaces/responses/Exposed/exposedListPensionIncreaseClaimsRes';
import { ExposedReadDocumentRes } from 'app/interfaces/responses/Exposed/exposedReadDocumentRes';
import { ErrorItem } from 'app/interfaces/support/status/errorItem';
import { PensionService } from 'app/services/api/pension/pension.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';

@Component({
    selector: 'app-pension-increase-claims',
    templateUrl: './pension-increase-claims.component.html',
    styleUrls: ['./pension-increase-claims.component.scss'],
})
export class PensionIncreaseClaimsComponent implements OnInit {
    // Table
    pageSizes = [5, 10, 25, 50, 100];
    dataSource = new MatTableDataSource<ExposedListKriaPensionIncreaseClaimsRes>();
    tableColumns: string[] = [];

    // New filter properties
    filterText: string = ''; // For text filter
    showOnlyWithDocumentId: boolean = false; // For toggle filter

    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    // Data
    pensionIncreaseClaims: Array<ExposedListKriaPensionIncreaseClaimsRes>;
    isLoading: boolean = false;

    // Form
    form: UntypedFormGroup;

    constructor(
        private pensionService: PensionService,
        private formBuilder: UntypedFormBuilder,
        private alertsService: AlertsService,
        private dialog: MatDialog,
        private loaderService: LoaderService
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.initTableColumns();
        this.listPensionIncreaseClaims();
    }

    ngAfterViewInit() {
        this.paginator.pageIndex = 0;
        this.paginator.pageSize = 10;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    initForm() {
        let startDate = new Date();
        startDate.setMonth(startDate.getMonth() + 1);
        startDate.setDate(1);

        this.form = this.formBuilder.group({
            startDate: new UntypedFormControl(new Date(startDate.toDateString()), [Validators.required]),
            endDate: new UntypedFormControl(new Date(startDate.toDateString()), [Validators.required]),
        });
    }

    listPensionIncreaseClaims() {
        if (!this.form.valid) {
            this.alertsService.changealertHeader('Villur í formi');
            this.alertsService.changeAlertErrorMessage('Vinsamlegast leysið úr villum í formi');
            this.alertsService.changeAlertErrorShow(true);
            return;
        }
        this.isLoading = true;
        this.pensionService
            .listPensionIncreaseClaims({
                EndDate: this.form.get('endDate').value,
                StartDate: this.form.get('startDate').value,
            })
            .then((res: Array<ExposedListKriaPensionIncreaseClaimsRes>) => {
                this.paginator.pageIndex = 0;
                this.pensionIncreaseClaims = res;
                this.isLoading = false;
                this.dataSource.data = this.pensionIncreaseClaims;
                this.dataSource.paginator = this.paginator;
                this.dataSource.sort = this.sort;
                this.applyFilter();
            });
    }

    initTableColumns() {
        this.tableColumns = [
            'ssn',
            'name',
            'totalPensionIncrease',
            'billCharge',
            'pensionScheduleDate',
            'scheduleNumber',
            'claimId',
            'receiptNumber',
            'actionNumber',
            'recordedDate',
            'recordedBy',
        ];
    }

    applyFilter() {
        const filterValue = this.filterText.trim().toLowerCase();

        this.dataSource.data = this.pensionIncreaseClaims.filter((claim) => {
            const matchesText = Object.values(claim).some((value) => value?.toString().toLowerCase().includes(filterValue));
            const matchesDocumentId = this.showOnlyWithDocumentId ? !claim.documentId : true;
            return matchesText && matchesDocumentId;
        });

        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    documentPensionIncreaseClaims(element) {
        this.pensionService
            .documentPensionIncreaseClaims({
                ClaimId: element.claimId,
                Date: element.pensionScheduleDate,
                Ssn: element.ssn,
            })
            .then((res: ExposedReadDocumentRes) => {
                var sampleArr = base64ToArrayBuffer(res.base64File);
                saveByteArray(element?.claimId != null ? 'lifeyrishaekkun_' + element?.claimId + '.pdf' : 'lifeyrishaekkun.pdf', sampleArr);
            });
    }

    createInvoicesPensionIncreaseClaims() {
        const dialogRef = this.dialog.open(DialogConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {
                title: 'Staðfesta aðgerð',
                message: 'Ertu viss um að þú viljir útfæra og senda reikninga?',
            } as DialogConfirmData,
        });

        dialogRef.afterClosed().subscribe(async (confirm: boolean) => {
            if (confirm) {
                const MAX_CONCURRENT_REQUESTS = 5;
                const queue: Promise<any>[] = [];
                let completedRequests = 0;
                let failedRequests: {
                    req: ExposedCreateInvoicePensionIncreaseReq;
                    error: ErrorItem;
                }[] = [];

                const filteredClaims = this.dataSource.filteredData.filter((claim) => !claim.documentId);
                const totalClaims = filteredClaims.length;

                if (totalClaims === 0) {
                    this.alertsService.changealertHeader('Aðgerð ekki framkvæmd');
                    this.alertsService.changeAlertErrorMessage('Reikningar þegar stofnaðir fyrir valdar færslur');
                    this.alertsService.changeAlertErrorShow(true);
                    return;
                }

                this.loaderService.changeActiveLoaders(1);
                this.loaderService.changeLoaderMessage(`Reikningar stofnaðir ${completedRequests}/${totalClaims}`);

                for (const claim of filteredClaims) {
                    if (queue.length >= MAX_CONCURRENT_REQUESTS) {
                        await Promise.race(queue);
                    }

                    var req = {
                        EndDate: this.form.get('endDate').value,
                        StartDate: this.form.get('startDate').value,
                        ActionNumber: claim.actionNumber,
                        ClaimId: claim.claimId,
                        Ssn: claim.ssn,
                    } as ExposedCreateInvoicePensionIncreaseReq;

                    const promise = this.pensionService
                        .createInvoicePensionIncrease(req)
                        .then(() => {
                            completedRequests++;
                        })
                        .catch((error: any) => {
                            failedRequests.push({
                                req,
                                error: error.errorItem,
                            });
                        })
                        .finally(() => {
                            this.loaderService.changeLoaderMessage(
                                `Stofna og sendi reikninga ${completedRequests + failedRequests.length}/${totalClaims}${failedRequests.length > 0 ? ` (${failedRequests.length} á villu)` : ''}`
                            );
                            queue.splice(queue.indexOf(promise), 1);
                        });

                    queue.push(promise);
                }

                await Promise.allSettled(queue);

                this.alertsService.changeAlertShowIcon(true);
                this.alertsService.changeAlertSuccessMessage('Reikningar stofnaðir og sendir');
                this.alertsService.changeAlertSuccessShow(true);
                this.listPensionIncreaseClaims();

                this.loaderService.changeActiveLoaders(-1);

                this.openResultsDialog(filteredClaims.length, completedRequests, failedRequests);
            }
        });
    }

    openResultsDialog(
        numTotal: number,
        numSuccess: number,
        failed: {
            req: ExposedCreateInvoicePensionIncreaseReq;
            error: ErrorItem;
        }[]
    ) {
        const dialogRef = this.dialog.open(DialogPensionIncreaseClaimsCreateResults, {
            autoFocus: false,
            restoreFocus: false,
            width: '650px',
            disableClose: true,
            data: {
                numberOfTotalClaims: numTotal,
                numberOfSuccessfullyCreatedInvoices: numSuccess,
                failedClaimInvoiceRequests: failed,
            },
        });

        dialogRef.afterClosed().subscribe((result) => { });
    }

    formatSsn(s) {
        return formatSsn(s);
    }
}
